import { DoctorAvatar } from "../components/DoctorAvatar";
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { HealthInsightsSection } from "../components/HealthInsightsSection";
import { 
  Search, 
  Calendar, 
  FlaskConical, 
  Heart, 
  ShieldCheck, 
  Award, 
  ChevronRight, 
  Clock, 
  MapPin, 
  CheckCircle2, 
  Star, 
  Sparkles, 
  Video, 
  Home, 
  Building2, 
  FileText, 
  ArrowRight,
  MessageCircle,
  Activity,
  Users,
  Percent,
  Phone
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function HomePage({ onOpenSearch, onBookDoctor, onBookTest }) {
  const navigate = useNavigate();
  const [popularTests, setPopularTests] = useState([]);
  const [packages, setPackages] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [forumTopics, setForumTopics] = useState([]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [testsRes, pkgRes, docRes, forumRes] = await Promise.all([
        axios.get(`${API}/diagnostics?popular_only=true`),
        axios.get(`${API}/packages?featured=true`),
        axios.get(`${API}/doctors`),
        axios.get(`${API}/forum/posts`)
      ]);
      setPopularTests(testsRes.data || []);
      setPackages(pkgRes.data || []);
      setDoctors(docRes.data || []);
      setForumTopics((forumRes.data.items || forumRes.data || []).slice(0, 3));
    } catch (err) {
      console.error("Error loading home page data:", err);
    }
  };

  const categories = ["All", "Pathology", "Digital X-Ray", "Ultrasound", "Doppler", "Cardiology", "Pulmonary"];

  const filteredTests = activeCategory === "All" 
    ? popularTests 
    : popularTests.filter(t => t.category === activeCategory);

  return (
    <div className="space-y-16 sm:space-y-24 pb-16">
      
      {/* 1. HERO SECTION — High Conversion Editorial Layout */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#EBF4F1] via-[#FBF9F4] to-[#FBF9F4] pt-10 sm:pt-16 pb-16 px-4 sm:px-8 border-b border-[#0D3B2E]/10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Text & Call-to-Actions */}
          <div className="lg:col-span-7 space-y-6 text-left">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-[#3B8271]/30 text-[#0D3B2E] text-xs font-semibold shadow-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span>Kolkata's Premier 4,000 Sq. Ft. Integrated Healthcare Centre</span>
            </div>

            <h1 className="font-serif-luxury text-4xl sm:text-5xl lg:text-6xl text-[#0D3B2E] font-bold tracking-tight leading-[1.12]">
              Comprehensive Healthcare. Diagnostics. Specialists. <br />
              <span className="italic font-normal text-[#3B8271]">All Under One Roof.</span>
            </h1>

            <p className="text-sm sm:text-base text-gray-600 max-w-xl leading-relaxed">
              Consult trusted Kolkata specialists, book diagnostic investigations and manage your healthcare journey — online or at our state-of-the-art Salt Lake & South Kolkata centre.
            </p>

            {/* Primary Action Buttons */}
            <div className="flex flex-wrap gap-3 pt-2">
              <button
                onClick={() => onBookDoctor ? onBookDoctor({ _picker: true }) : navigate("/doctors")}
                data-testid="hero-book-appointment-btn"
                className="px-6 py-3.5 rounded-full bg-[#0D3B2E] hover:bg-[#165644] text-white font-semibold text-xs sm:text-sm tracking-wide shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
              >
                <Calendar className="w-4 h-4 text-amber-300" />
                Book an Appointment
              </button>

              <button
                onClick={() => onBookTest ? onBookTest(popularTests[0]) : navigate("/diagnostics")}
                data-testid="hero-book-diagnostic-btn"
                className="px-6 py-3.5 rounded-full bg-[#3B8271] hover:bg-[#2e6b5d] text-white font-semibold text-xs sm:text-sm tracking-wide shadow-md transition-all flex items-center gap-2"
              >
                <FlaskConical className="w-4 h-4 text-amber-200" />
                Book a Diagnostic Test
              </button>

              <Link
                to="/packages"
                data-testid="hero-explore-packages-btn"
                className="px-5 py-3.5 rounded-full bg-white hover:bg-gray-50 text-[#0D3B2E] border border-[#0D3B2E]/30 font-semibold text-xs sm:text-sm transition-all flex items-center gap-1.5 shadow-sm"
              >
                <span>Explore Health Packages</span>
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Search Input Trigger */}
            <div className="pt-4">
              <button
                onClick={onOpenSearch}
                data-testid="hero-search-bar"
                className="w-full max-w-xl p-3 sm:p-4 bg-white rounded-2xl border-2 border-[#0D3B2E]/20 shadow-lg hover:border-[#0D3B2E] transition-all flex items-center justify-between text-left group"
              >
                <div className="flex items-center gap-3">
                  <Search className="w-5 h-5 text-[#3B8271] group-hover:scale-110 transition-transform" />
                  <div>
                    <p className="text-xs sm:text-sm font-semibold text-gray-800">What are you looking for?</p>
                    <p className="text-[11px] text-gray-500">Search Doctor, Specialty, Test (2D Echo, CBC), Package, or Concern</p>
                  </div>
                </div>
                <span className="text-xs bg-[#0D3B2E] text-white px-4 py-2 rounded-xl font-medium shrink-0 shadow-sm">
                  Search Now
                </span>
              </button>
            </div>

            {/* Key Trust Signals */}
            <div className="grid grid-cols-3 gap-3 pt-4 border-t border-[#0D3B2E]/10 max-w-lg">
              <div>
                <p className="font-serif-luxury text-xl font-bold text-[#0D3B2E]">50+</p>
                <p className="text-[11px] text-gray-500">Diagnostic Tests</p>
              </div>
              <div>
                <p className="font-serif-luxury text-xl font-bold text-[#0D3B2E]">8+ OPDs</p>
                <p className="text-[11px] text-gray-500">Specialist Clinics</p>
              </div>
              <div>
                <p className="font-serif-luxury text-xl font-bold text-[#0D3B2E]">Same Day</p>
                <p className="text-[11px] text-gray-500">Digital Reports</p>
              </div>
            </div>
          </div>

          {/* Right Hero Visual Card */}
          <div className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md bg-white p-3 rounded-3xl shadow-2xl border border-emerald-100 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1758691463198-dc663b8a64e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtZWRpY2FsJTIwY2xpbmljJTIwZG9jdG9yJTIwY29uc3VsdGF0aW9ufGVufDB8fHx8MTc4NTkxMjU1NHww&ixlib=rb-4.1.0&q=85"
                alt="6SigmaCare Doctor Consultation"
                className="w-full h-72 sm:h-80 object-cover rounded-2xl"
              />

              {/* Floating Mini Badges */}
              <div className="absolute top-6 left-6 bg-[#0D3B2E]/95 backdrop-blur-md text-white p-3 rounded-2xl shadow-xl border border-emerald-500/30 flex items-center gap-2.5">
                <ShieldCheck className="w-5 h-5 text-amber-300 shrink-0" />
                <div>
                  <p className="text-xs font-bold">NABL Standard Laboratory</p>
                  <p className="text-[10px] text-emerald-200">AERB Approved Low-Dose Imaging</p>
                </div>
              </div>

              <div className="absolute bottom-6 right-6 bg-white/95 backdrop-blur-md p-3 rounded-2xl shadow-xl border border-gray-100 flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center font-bold">
                  <Heart className="w-4 h-4 fill-rose-600" />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-900">Dedicated Cardiac Wing</p>
                  <p className="text-[10px] text-[#3B8271] font-semibold">2D Echo • Stress ECG • Holter</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* 2. QUICK BOOKING MATRIX — 4 Key Healthcare Pillars */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          
          <div 
            onClick={() => navigate("/doctors")}
            data-testid="matrix-card-doctors"
            className="p-6 bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer group"
          >
            <div className="w-12 h-12 rounded-2xl bg-[#EBF4F1] text-[#0D3B2E] flex items-center justify-center mb-4 group-hover:bg-[#0D3B2E] group-hover:text-amber-300 transition-colors">
              <Users className="w-6 h-6" />
            </div>
            <h3 className="font-serif-luxury text-xl font-bold text-gray-900 mb-1">Specialist OPD</h3>
            <p className="text-xs text-gray-500 leading-relaxed mb-4">
              Cardiology, Diabetology, Gynaecology, Neurology & Pulmonology consultations.
            </p>
            <span className="text-xs font-bold text-[#0D3B2E] flex items-center gap-1 group-hover:gap-2 transition-all">
              Book Doctor Appointment <ChevronRight className="w-4 h-4" />
            </span>
          </div>

          <div 
            onClick={() => navigate("/diagnostics")}
            data-testid="matrix-card-diagnostics"
            className="p-6 bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer group"
          >
            <div className="w-12 h-12 rounded-2xl bg-[#EBF4F1] text-[#0D3B2E] flex items-center justify-center mb-4 group-hover:bg-[#0D3B2E] group-hover:text-amber-300 transition-colors">
              <FlaskConical className="w-6 h-6" />
            </div>
            <h3 className="font-serif-luxury text-xl font-bold text-gray-900 mb-1">Pathology & Lab</h3>
            <p className="text-xs text-gray-500 leading-relaxed mb-4">
              Fully automated blood counts, biochemistry, thyroid, hormones & cultures.
            </p>
            <span className="text-xs font-bold text-[#0D3B2E] flex items-center gap-1 group-hover:gap-2 transition-all">
              Book Lab Test <ChevronRight className="w-4 h-4" />
            </span>
          </div>

          <div 
            onClick={() => navigate("/cardiology")}
            data-testid="matrix-card-cardiology"
            className="p-6 bg-rose-50/50 rounded-3xl border border-rose-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer group"
          >
            <div className="w-12 h-12 rounded-2xl bg-rose-100 text-rose-700 flex items-center justify-center mb-4 group-hover:bg-rose-700 group-hover:text-white transition-colors">
              <Heart className="w-6 h-6 fill-current" />
            </div>
            <h3 className="font-serif-luxury text-xl font-bold text-gray-900 mb-1">Heart & Cardiac</h3>
            <p className="text-xs text-gray-500 leading-relaxed mb-4">
              2D Echo Doppler, TMT treadmill stress testing, ECG and 24-hr Holter.
            </p>
            <span className="text-xs font-bold text-rose-800 flex items-center gap-1 group-hover:gap-2 transition-all">
              Explore Heart Care <ChevronRight className="w-4 h-4" />
            </span>
          </div>

          <div 
            onClick={() => navigate("/packages")}
            data-testid="matrix-card-packages"
            className="p-6 bg-amber-50/50 rounded-3xl border border-amber-200/60 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer group"
          >
            <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-800 flex items-center justify-center mb-4 group-hover:bg-amber-600 group-hover:text-white transition-colors">
              <Percent className="w-6 h-6" />
            </div>
            <h3 className="font-serif-luxury text-xl font-bold text-gray-900 mb-1">Health Packages</h3>
            <p className="text-xs text-gray-500 leading-relaxed mb-4">
              Full body annual checkups, senior citizen & women's wellness packages up to 58% off.
            </p>
            <span className="text-xs font-bold text-amber-900 flex items-center gap-1 group-hover:gap-2 transition-all">
              View All Packages <ChevronRight className="w-4 h-4" />
            </span>
          </div>

        </div>
      </section>

      {/* 3. POPULAR INVESTIGATIONS CATALOGUE PREVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#3B8271]">Diagnostic Excellence</span>
            </div>
            <h2 className="font-serif-luxury text-3xl sm:text-4xl text-[#0D3B2E] font-bold">
              Popular Investigations
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              Select tests with transparent Kolkata pricing, preparation guidelines and fast digital delivery.
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-1.5 p-1 bg-gray-100 rounded-2xl text-xs">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                data-testid={`home-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-3.5 py-1.5 rounded-xl font-medium transition-all ${
                  activeCategory === cat 
                    ? "bg-[#0D3B2E] text-white shadow" 
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Tests Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredTests.slice(0, 6).map((test) => (
            <div
              key={test.id}
              data-testid={`popular-test-card-${test.id}`}
              className="p-5 bg-white rounded-3xl border border-gray-200/80 shadow-sm hover:shadow-xl transition-all flex flex-col justify-between group"
            >
              <div className="space-y-3">
                <div className="flex justify-between items-start">
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#EBF4F1] text-[#0D3B2E]">
                    {test.category}
                  </span>
                  <span className="text-[11px] text-emerald-800 font-bold bg-emerald-50 px-2 py-0.5 rounded-full">
                    {test.discount_pct}% OFF
                  </span>
                </div>

                <h3 className="font-serif-luxury text-lg font-bold text-gray-900 group-hover:text-[#0D3B2E] transition-colors line-clamp-2">
                  {test.name}
                </h3>

                <p className="text-xs text-gray-500 line-clamp-2">{test.description}</p>

                <div className="space-y-1 text-[11px] text-gray-600 pt-2 border-t border-gray-100">
                  <p className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-[#3B8271]" />
                    TAT: <b>{test.tat}</b>
                  </p>
                  <p className="text-[11px]">
                    Fasting: <b>{test.fasting_required ? "Yes (Overnight)" : "No Fasting Needed"}</b>
                  </p>
                </div>
              </div>

              <div className="pt-4 mt-4 border-t border-gray-100 flex items-center justify-between">
                <div>
                  <span className="text-xs text-gray-400 line-through mr-2">₹{test.mrp}</span>
                  <span className="font-serif-luxury text-xl font-bold text-[#0D3B2E]">₹{test.price}</span>
                </div>

                <button
                  onClick={() => onBookTest ? onBookTest(test) : navigate(`/diagnostics?test=${test.id}`)}
                  data-testid={`book-test-btn-${test.id}`}
                  className="px-4 py-2 bg-[#0D3B2E] hover:bg-[#165644] text-white text-xs font-semibold rounded-xl transition-colors"
                >
                  Book Test
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link
            to="/diagnostics"
            data-testid="view-all-tests-btn"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-[#0D3B2E] text-[#0D3B2E] rounded-full text-xs font-bold hover:bg-[#0D3B2E] hover:text-white transition-all shadow-sm"
          >
            Explore Complete 50+ Diagnostic Catalogue <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* 4. DEDICATED CARDIAC DIAGNOSTICS SPOTLIGHT */}
      <section className="bg-gradient-to-r from-[#0D3B2E] to-[#07221A] text-white py-14 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          
          <div className="lg:col-span-7 space-y-4">
            <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-amber-400 text-[#07221A]">
              Specialised Clinical Wing
            </span>
            <h2 className="font-serif-luxury text-3xl sm:text-5xl font-bold text-white leading-tight">
              Heart & Cardiac Diagnostics Centre
            </h2>
            <p className="text-xs sm:text-sm text-emerald-100/90 leading-relaxed max-w-xl">
              Advanced non-invasive cardiac evaluation supervised by veteran Kolkata cardiologists. Early detection of ischaemia, arrhythmias, valve disorders and hypertension.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2">
              <div className="p-3 bg-emerald-900/40 rounded-2xl border border-emerald-700/50">
                <p className="text-xs font-bold text-amber-300">2D Echo Colour Doppler</p>
                <p className="text-[10px] text-gray-300">Ejection fraction & wall motion</p>
              </div>
              <div className="p-3 bg-emerald-900/40 rounded-2xl border border-emerald-700/50">
                <p className="text-xs font-bold text-amber-300">TMT Stress Test</p>
                <p className="text-[10px] text-gray-300">Treadmill exercise ECG</p>
              </div>
              <div className="p-3 bg-emerald-900/40 rounded-2xl border border-emerald-700/50">
                <p className="text-xs font-bold text-amber-300">24-Hr Holter & ABPM</p>
                <p className="text-[10px] text-gray-300">Extended rhythm & BP monitoring</p>
              </div>
            </div>

            <div className="pt-4 flex flex-wrap gap-3">
              <Link
                to="/cardiology"
                data-testid="cardiology-wing-cta"
                className="px-6 py-3 rounded-full bg-amber-400 hover:bg-amber-300 text-[#07221A] font-bold text-xs shadow-lg transition-all flex items-center gap-2"
              >
                <Heart className="w-4 h-4 fill-current" />
                Explore Heart Care Wing
              </Link>
              <button
                onClick={() => onBookDoctor ? onBookDoctor({ _picker: true, specialty: "Cardiology" }) : navigate("/doctors")}
                className="px-5 py-3 rounded-full bg-emerald-800/80 hover:bg-emerald-700 text-white font-semibold text-xs border border-emerald-600 transition-all"
              >
                Consult Senior Cardiologist
              </button>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="p-2 bg-white/10 backdrop-blur-md rounded-3xl border border-white/20 overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1691933880096-4046bc9e2fa0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxtZWRpY2FsJTIwdWx0cmFzb3VuZCUyMGRpYWdub3N0aWMlMjBlcXVpcG1lbnR8ZW58MHx8fHwxNzgyNTU5NjU5fDA&ixlib=rb-4.1.0&q=85"
                alt="Ultrasound & Echo Machine"
                className="w-full h-64 sm:h-72 object-cover rounded-2xl"
              />
            </div>
          </div>

        </div>
      </section>

      {/* 5. PREVENTIVE HEALTH PACKAGES — Commercial Feature */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10 space-y-2">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#3B8271]">Annual Preventive Care</span>
          <h2 className="font-serif-luxury text-3xl sm:text-4xl text-[#0D3B2E] font-bold">
            Comprehensive Health Packages
          </h2>
          <p className="text-xs sm:text-sm text-gray-600">
            Formulated by senior clinicians with up to 58% savings compared to individual test MRPs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.slice(0, 12).map((pkg) => (
            <div
              key={pkg.id}
              data-testid={`pkg-card-${pkg.id}`}
              className="bg-white rounded-3xl border-2 border-gray-100 hover:border-[#0D3B2E] shadow-sm hover:shadow-2xl transition-all p-6 flex flex-col justify-between relative overflow-hidden"
            >
              {pkg.popular && (
                <div className="absolute top-0 right-0 bg-amber-400 text-[#07221A] text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-bl-xl shadow-sm">
                  Most Popular
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#3B8271] bg-[#EBF4F1] px-2 py-0.5 rounded">
                    {pkg.category}
                  </span>
                  <h3 className="font-serif-luxury text-xl font-bold text-gray-900 mt-2">{pkg.name}</h3>
                  <p className="text-xs text-gray-500 mt-1">{pkg.tagline}</p>
                </div>

                <div className="p-3 bg-[#FBF9F4] rounded-2xl border border-gray-200">
                  <div className="flex items-baseline justify-between">
                    <div>
                      <span className="text-xs text-gray-400 line-through">MRP ₹{pkg.mrp}</span>
                      <p className="font-serif-luxury text-2xl font-bold text-[#0D3B2E]">₹{pkg.price}</p>
                    </div>
                    <span className="text-xs font-bold text-emerald-800 bg-emerald-100 px-2 py-1 rounded-full">
                      Save {pkg.discount_pct}%
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1">Includes <b>{pkg.tests_count} Essential Tests</b>{(pkg.inclusions || []).length > 0 ? ` + ${pkg.inclusions.map((i) => i.name).join(", ")}` : ""}</p>
                </div>

                <div className="space-y-1.5 pt-2">
                  <p className="text-[11px] font-bold text-gray-800 uppercase tracking-wider">Key Inclusions:</p>
                  {pkg.included_tests.slice(0, 5).map((t, idx) => (
                    <p key={idx} className="text-xs text-gray-600 flex items-start gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#3B8271] shrink-0 mt-0.5" />
                      <span>{t}</span>
                    </p>
                  ))}
                  {pkg.included_tests.length > 5 && (
                    <p className="text-[11px] text-[#3B8271] font-semibold pl-5">
                      + {pkg.included_tests.length - 5} more diagnostic parameters
                    </p>
                  )}
                </div>
              </div>

              <div className="pt-6 mt-6 border-t border-gray-100 flex gap-2">
                <Link
                  to={`/packages#${pkg.slug}`}
                  className="flex-1 py-2.5 text-center bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl text-xs font-semibold transition-colors"
                >
                  View Details
                </Link>
                <button
                  onClick={() => navigate(`/packages#${pkg.slug}`)}
                  data-testid={`book-pkg-btn-${pkg.id}`}
                  className="flex-1 py-2.5 bg-[#0D3B2E] hover:bg-[#165644] text-white rounded-xl text-xs font-semibold transition-colors"
                >
                  Book Package
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 6. SPECIALIST DOCTORS SHOWCASE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-[#3B8271]">Consultation Chambers</span>
            <h2 className="font-serif-luxury text-3xl sm:text-4xl text-[#0D3B2E] font-bold">
              Find Trusted Specialists
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              Top Kolkata medical faculty available for in-person clinic OPD and high-definition video calls.
            </p>
          </div>

          <Link
            to="/doctors"
            data-testid="all-doctors-link"
            className="text-xs font-bold text-[#0D3B2E] flex items-center gap-1 hover:gap-2 transition-all"
          >
            View All 8+ Specialists <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {doctors.slice(0, 4).map((doc) => (
            <div
              key={doc.id}
              data-testid={`home-doc-card-${doc.id}`}
              className="bg-white rounded-3xl border border-gray-200 shadow-sm hover:shadow-xl transition-all p-5 flex flex-col justify-between group"
            >
              <div>
                <div className="relative mb-4">
                  <DoctorAvatar
                    doctor={doc}
                    className="w-full h-48 rounded-2xl group-hover:scale-102 transition-transform"
                    textClass="text-5xl"
                  />
                  <div className="absolute top-2 right-2 bg-white/95 backdrop-blur-md px-2 py-0.5 rounded-full text-[10px] font-bold text-amber-900 flex items-center gap-1 shadow">
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                    {doc.rating} ({doc.reviews_count})
                  </div>
                </div>

                <span className="text-[10px] font-bold uppercase tracking-wider text-[#3B8271] bg-[#EBF4F1] px-2 py-0.5 rounded">
                  {doc.specialty}
                </span>

                <h3 className="font-serif-luxury text-base font-bold text-gray-900 mt-1.5 line-clamp-1">
                  {doc.name}
                </h3>
                <p className="text-[11px] text-gray-500 line-clamp-1">{doc.qualification}</p>
                <p className="text-[11px] text-emerald-800 font-medium mt-1">Exp: <b>{doc.experience_years}+ Years</b></p>
              </div>

              <div className="pt-4 mt-4 border-t border-gray-100 flex items-center justify-between">
                <div>
                  <span className="text-[10px] text-gray-400 block">Consultation Fee</span>
                  <span className="font-bold text-xs text-[#0D3B2E]">₹{doc.physical_fee}</span>
                </div>

                <button
                  onClick={() => onBookDoctor ? onBookDoctor(doc) : navigate("/doctors")}
                  data-testid={`home-book-doc-${doc.id}`}
                  className="px-3.5 py-1.5 bg-[#0D3B2E] hover:bg-[#165644] text-white text-xs font-semibold rounded-xl transition-colors"
                >
                  Book Slot
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 7. 4,000 SQ. FT. CENTRE INFRASTRUCTURE & DEPARTMENTS */}
      <section className="bg-[#EBF4F1] py-14 px-4 sm:px-8 border-y border-[#3B8271]/20">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-[#0D3B2E]">Designed for Patient Comfort</span>
            <h2 className="font-serif-luxury text-3xl sm:text-4xl text-[#0D3B2E] font-bold">
              4,000 Sq. Ft. Integrated Facility
            </h2>
            <p className="text-xs sm:text-sm text-gray-600">
              Spacious, climate-controlled polyclinic with zero queue congestion, sterile sampling bays and wheelchair accessibility in Salt Lake.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { title: "Specialist OPD Suites", sub: "5 Soundproof Chambers", icon: Building2 },
              { title: "Automated Pathology", sub: "VITEK & 6-Part Cell Analyzers", icon: FlaskConical },
              { title: "Low-Dose Digital X-Ray", sub: "High-Freq DR System", icon: Award },
              { title: "3D/4D Ultrasound", sub: "Dedicated USG & Doppler", icon: Activity },
              { title: "Cardiac Stress Lab", sub: "2D Echo & TMT Treadmill", icon: Heart },
              { title: "Neuro & PFT Cabin", sub: "Digital Video EEG & Spirometry", icon: ShieldCheck }
            ].map((dept, idx) => {
              const IconComp = dept.icon;
              return (
                <div key={idx} className="p-4 bg-white rounded-2xl border border-emerald-100 shadow-sm text-center space-y-2">
                  <div className="w-10 h-10 rounded-xl bg-[#0D3B2E] text-amber-300 flex items-center justify-center mx-auto">
                    <IconComp className="w-5 h-5" />
                  </div>
                  <h4 className="text-xs font-bold text-gray-900">{dept.title}</h4>
                  <p className="text-[10px] text-gray-500">{dept.sub}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 8. HOW IT WORKS — 5 Simple Steps */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10 space-y-2">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#3B8271]">Seamless Patient Journey</span>
          <h2 className="font-serif-luxury text-3xl sm:text-4xl text-[#0D3B2E] font-bold">
            How It Works
          </h2>
          <p className="text-xs sm:text-sm text-gray-600">
            From seamless booking to verified digital health reports delivered straight to your account.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
          {[
            { step: "1", title: "Choose Service", desc: "Select Doctor, Diagnostic Test or Health Package." },
            { step: "2", title: "Select Live Slot", desc: "Pick your preferred date & time with instant calendar sync." },
            { step: "3", title: "Easy Payment", desc: "Pay securely via Razorpay UPI/Cards or Pay at Desk." },
            { step: "4", title: "Visit / Home Sample", desc: "Visit our Kolkata centre or opt for home phlebotomy." },
            { step: "5", title: "Digital Reports", desc: "Access verified reports anytime on My Health dashboard." }
          ].map((item) => (
            <div key={item.step} className="p-5 bg-white rounded-3xl border border-gray-100 shadow-sm text-center relative space-y-2">
              <span className="w-8 h-8 rounded-full bg-[#0D3B2E] text-amber-300 font-bold text-xs flex items-center justify-center mx-auto shadow">
                {item.step}
              </span>
              <h4 className="font-serif-luxury text-base font-bold text-gray-900">{item.title}</h4>
              <p className="text-[11px] text-gray-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <HealthInsightsSection />

      {/* 9. INTERACTIVE FORUM PREVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="bg-white rounded-3xl border border-gray-200 p-6 sm:p-10 shadow-sm">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-900 px-2 py-0.5 rounded">
                  Community Hub
                </span>
              </div>
              <h3 className="font-serif-luxury text-2xl sm:text-3xl font-bold text-[#0D3B2E] mt-1">
                6SigmaCare Patient Community Forum
              </h3>
              <p className="text-xs text-gray-500">
                Read patient discussions, ask medical preparation questions and explore verified clinical responses.
              </p>
            </div>

            <Link
              to="/forum"
              data-testid="home-forum-cta-btn"
              className="px-5 py-2.5 rounded-full bg-[#0D3B2E] text-white text-xs font-semibold hover:bg-[#165644] transition-colors shrink-0"
            >
              Explore Forum Discussions
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {forumTopics.map((post) => (
              <div
                key={post.id}
                onClick={() => navigate("/forum")}
                className="p-4 bg-[#FBF9F4] rounded-2xl border border-gray-100 hover:border-[#3B8271] cursor-pointer transition-all space-y-2"
              >
                <span className="text-[10px] font-bold text-[#0D3B2E] bg-white px-2 py-0.5 rounded border border-gray-200">
                  {post.category}
                </span>
                <h4 className="text-xs font-bold text-gray-900 line-clamp-2">{post.title}</h4>
                <p className="text-[11px] text-gray-500 line-clamp-2">{post.content}</p>
                <div className="pt-2 flex justify-between items-center text-[10px] text-gray-400">
                  <span>By {post.author_name}</span>
                  <span className="text-emerald-800 font-bold">{post.upvotes} Upvotes</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10. LOCATION, TIMINGS & GOOGLE MAPS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="bg-[#07221A] text-white rounded-3xl p-6 sm:p-10 border border-[#165644]/40 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-6 space-y-4">
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-400">Centres in Kolkata</span>
            <h3 className="font-serif-luxury text-3xl font-bold text-white">
              Visit 6SigmaCare Polyclinic & Diagnostics
            </h3>
            <p className="text-xs text-gray-300 leading-relaxed">
              Conveniently located near Salt Lake Sector V with ample dedicated patient parking, elevator access and ground-floor diagnostic reception.
            </p>

            <div className="space-y-2 text-xs text-gray-300">
              <p className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-amber-400 shrink-0" />
                <span>Sector V, Salt Lake & Ballygunge Circular Rd, Kolkata 700091</span>
              </p>
              <p className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>Open 7 Days: <b>7:00 AM – 9:00 PM</b></span>
              </p>
              <p className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>Enquiries & Helplines: <b>+91 98300 12345 / 033-2357-8900</b></span>
              </p>
            </div>

            <div className="pt-2 flex gap-3">
              <a
                href="https://maps.google.com/?q=Salt+Lake+Sector+V+Kolkata"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="get-directions-btn"
                className="px-5 py-2.5 rounded-full bg-amber-400 text-[#07221A] font-bold text-xs hover:bg-amber-300 transition-colors"
              >
                Get Google Maps Directions
              </a>
              <Link
                to="/about"
                className="px-5 py-2.5 rounded-full bg-emerald-900 text-white font-semibold text-xs border border-emerald-700 hover:bg-emerald-800 transition-colors"
              >
                Centre Infrastructure Tour
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="h-64 sm:h-72 w-full rounded-2xl overflow-hidden border border-emerald-700/50 relative shadow-2xl">
              <iframe
                title="6SigmaCare Kolkata Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14736.292881223947!2d88.425!3d22.58!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a0275b020703c0d%3A0xe69e3f576f37f37f!2sSector%20V%2C%20Bidhannagar%2C%20Kolkata%2C%20West%20Bengal!5e0!3m2!1sen!2sin!4v1650000000000!5m2!1sen!2sin"
                className="w-full h-full border-0"
                loading="lazy"
                allowFullScreen=""
              ></iframe>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
